import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "../utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  className?: string;
  onSearchChange?: (query: string) => void;
  isLoading?: boolean;
  loadingText?: string;
}

const Combobox = React.forwardRef<
  React.ElementRef<typeof Button>,
  ComboboxProps
>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = "Select option...",
      searchPlaceholder = "Search...",
      emptyText = "No option found.",
      disabled = false,
      readOnly = false,
      error = false,
      className,
      onSearchChange,
      isLoading = false,
      loadingText = "Loading...",
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const [triggerWidth, setTriggerWidth] = React.useState<number>(0);
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);

    const selectedOption = options.find(option => option.value === value);

    const onSearchValueChange = (query: string) => {
      setSearchQuery(query);
      onSearchChange?.(query);
    };

    React.useEffect(() => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    }, []);

    return (
      <Popover open={readOnly ? false : open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={node => {
              if (typeof ref === "function") {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }

              triggerRef.current = node;
            }}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={error || undefined}
            data-readonly={readOnly || undefined}
            className={cn(
              "qodo qodo-combobox w-full h-9 px-3 py-1 justify-between rounded-md",
              "border border-purple-muted-500 bg-purple-muted-700",
              "text-sm text-purple-muted-300 selection:bg-primary selection:text-primary-foreground",
              "font-normal shadow-xs transition-[color,box-shadow] outline-none focus-visible:!ring-ring/50 focus-visible:!ring-[3px] focus-visible:!ring-offset-0",
              "aria-invalid:!border-destructive aria-invalid:!ring-destructive/20 dark:aria-invalid:!ring-destructive/40",
              "disabled:!cursor-not-allowed disabled:!pointer-events-auto disabled:opacity-50 disabled:!bg-purple-muted-700 disabled:!border-purple-muted-500 disabled:!text-purple-muted-300 disabled:hover:!bg-purple-muted-700 disabled:hover:!text-purple-muted-300",
              "data-[readonly]:opacity-100 data-[readonly]:bg-purple-muted-700 data-[readonly]:border-purple-muted-400 data-[readonly]:cursor-default data-[readonly]:text-purple-muted-200 data-[readonly]:hover:bg-purple-muted-700 data-[readonly]:hover:text-purple-muted-200",
              {
                "text-muted-foreground": !selectedOption,
              },
              className,
            )}
            disabled={disabled}
            {...props}
          >
            {selectedOption ? selectedOption.label : placeholder}
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 border bg-fill-interactive-neutral-idle border-purple-muted-500 text-purple-muted-300"
          style={{ width: triggerWidth > 0 ? `${triggerWidth}px` : "auto" }}
        >
          <Command
            className="w-full bg-fill-interactive-neutral-idle"
            shouldFilter={!onSearchChange}
          >
            <CommandInput
              placeholder={searchPlaceholder}
              {...(!!onSearchChange && {
                value: searchQuery,
                onValueChange: onSearchValueChange,
              })}
            />
            <CommandList>
              {isLoading ? (
                <CommandLoading>{loadingText}</CommandLoading>
              ) : (
                <>
                  <CommandEmpty className="flex justify-center items-center p-6 text-center text-sm text-purple-muted-300">
                    {emptyText}
                  </CommandEmpty>
                  <CommandGroup>
                    {options.map(option => {
                      const isSelected = value === option.value;
                      return (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          keywords={[option.label]}
                          disabled={option.disabled}
                          className={cn(
                            "text-purple-100 hover:bg-purple-muted-500",
                            "focus:bg-purple-muted-500 focus:text-purple-100",
                            {
                              "bg-purple-muted-500": isSelected,
                            },
                          )}
                          onSelect={currentValue => {
                            if (currentValue !== value) {
                              onValueChange?.(
                                currentValue === value ? "" : currentValue,
                              );
                            }
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={cn("mr-2 size-4", {
                              "opacity-100": isSelected,
                              "opacity-0": !isSelected,
                            })}
                          />
                          {option.label}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

Combobox.displayName = "Combobox";

export { Combobox };
